import React, { useMemo, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import type { UserAccess } from '../../store/slices/manageAccessSlice';
import CardTable from '../../components/common/CardTable';
import Table, { TableColumn, TableRow } from '../../components/common/Table/Table';
import Pagination from '../../components/common/Table/Pagination';
import NoResultsFound from '../../components/common/NoResultsFound';

// Column configuration
const columns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'access', label: 'Access' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
];

interface ManageAccessTableProps {
  onEdit?: (user: UserAccess) => void;
  onDelete?: (user: UserAccess) => void;
  onPageChange: (page: number) => void;
  editingUserKey?: string | null;
}

export const buildManageAccessRowKey = (user: UserAccess): string => {
  return [
    user.nuid,
    user.privRole,
    user.responsibilityRegion ?? "",
    user.responsibilityArea ?? "",
    user.responsibilityCenter ?? "",
    user.responsibilityLocation ?? "",
  ].join("::");
};

const ManageAccessTable: React.FC<ManageAccessTableProps> = ({ 
  onEdit, 
  onDelete,
  onPageChange,
  editingUserKey 
}) => {
  const { users, totalItems, currentPage, perPage } = useAppSelector((state) => state.manageAccess);
  const loggedInNuid = useAppSelector((state) => state.employee?.employeeDetails?.nuid);
  
  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    onPageChange(page);
  }, [onPageChange]);

  // Memoized edit handler
  const handleEdit = useCallback((user: UserAccess) => {
    onEdit?.(user);
  }, [onEdit]);

  // Memoized delete handler
  const handleDelete = useCallback((user: UserAccess) => {
    onDelete?.(user);
  }, [onDelete]);

  // Check if a user is being edited
  const isUserEditing = useCallback((user: UserAccess) => {
    return editingUserKey === buildManageAccessRowKey(user);
  }, [editingUserKey]);

  // Memoize row data to prevent unnecessary recalculations
  const rows: TableRow[] = useMemo(() => users.map((user) => {
    const isEditing = isUserEditing(user);
    const editButtonClass = isEditing
      ? 'button py-0! flex items-center group bg-[#003B71]! text-white! hover:bg-[#003B71]! hover:text-white! focus:bg-[#003B71]! focus:text-white! [&>img]:brightness-0! [&>img]:invert!'
      : 'button py-0! flex items-center group text-[#003B71]! hover:bg-[#003B71]! hover:text-white! focus:bg-[#003B71]! focus:text-white!';
    const isSelf = user.nuid && loggedInNuid && String(user.nuid) === String(loggedInNuid);
    return {
      name: `${user.lastName}, ${user.firstName} - ${user.nuid}`,
      role: user.privRole,
      access: (
        <span>
          Area: {user.responsibilityAreaDesc ?? 'All'}<br />
          Center: {user.responsibilityCenterDesc ?? 'All'}<br />
          Location: {user.responsibilityLocationDesc ?? 'All'}
        </span>
      ),
      edit: isSelf ? null : (
        <span className="table-text">
          <button
            type="button"
            className={editButtonClass}
            onClick={() => handleEdit(user)}
            aria-label={`${isEditing ? 'Editing' : 'Edit'} ${user.lastName} ${user.firstName}`}
          >
            <img 
              src="/assets/images/pencil-outline.svg" 
              alt="" 
              className="w-6 h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert!"
            />{' '}
            {isEditing ? 'Editing' : 'Edit'}
          </button>
        </span>
      ),
      delete: isSelf ? null : (
        <span className="table-text">
          <button 
            type="button" 
            className="button py-0! flex items-center group hover:text-white! focus:text-white!"
            onClick={() => handleDelete(user)}
            aria-label={`Delete ${user.lastName} ${user.firstName}`}
          >
            <img 
              src="/assets/images/trash-outline.svg" 
              alt="" 
              className="w-6 h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert!"
            />{' '}
            Delete
          </button>
        </span>
      ),
      id: buildManageAccessRowKey(user),
    };
  }), [users, handleEdit, handleDelete, isUserEditing, loggedInNuid]);

  return (
    <div>
      <CardTable>
        {rows.length > 0 ? (
          <>
            <Table
              columns={columns}
              rows={rows}
            />
            <div className="mt-4 left-0 mobile:relative">
              <Pagination
                totalItems={totalItems}
                itemsPerPage={perPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        ) : (
          <div className="mt-10!">
            <NoResultsFound title="No users found." message="" />
          </div>
        )}
      </CardTable>
    </div>
  );
};

export default ManageAccessTable;
